/**
 * Shared logic for opportunity groups (parent + child slots).
 *
 * Feed ranks bookable slots (children + standalone), then collapses to parent id for display.
 * SQL helpers in `server/src/entities/opportunity/opportunity-group-feed-sql.ts`.
 */

import { hasNoCoordinates } from "@shared/helpers/geo";
import { opportunitySeatsAreFull } from "@shared/helpers/opportunity-occupancy";

/** Matches feed `hasEnoughSpace` constraint in `opportunity.service.ts`. */
export const FEED_MIN_FREE_SPACES = 1;

type DateLike = Date | string | null | undefined;

function toTimestamp(value: DateLike): number | null {
  if (value == null) return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

export type OpportunityGroupChildSnapshot = {
  startDate: Date | null;
  endDate: Date | null;
  lastCallToVolunteers: Date;
  coordinates: unknown;
  isRemote: boolean;
  location: string;
  /** When set, sync uses feed-aligned volunteer visibility (not date-only). */
  isDeleted?: boolean;
  isClosed?: boolean;
  isFull?: boolean;
  status?: string | null;
  participantsCount?: number;
  amount?: number;
};

export type ParentSyncFromChildrenPayload = {
  startDate: Date | null;
  endDate: Date | null;
  lastCallToVolunteers: Date;
  coordinates: unknown;
  isRemote: boolean;
  location: string;
};

function groupChildLocationKey(child: {
  location: string;
  isRemote: boolean;
}): string {
  if (child.isRemote) return "__remote__";
  return child.location.trim().toLowerCase();
}

/** True when volunteer-visible slots span more than one location (incl. remote vs physical). */
export function groupParentHasMultipleLocations(
  children: Pick<OpportunityGroupChildSnapshot, "location" | "isRemote">[],
): boolean {
  return groupParentUniqueLocationCount(children) > 1;
}

function groupChildDateKey(child: {
  startDate: DateLike;
  endDate: DateLike;
}): string {
  const start = toTimestamp(child.startDate) ?? "null";
  const end = toTimestamp(child.endDate) ?? "null";
  return `${start}|${end}`;
}

/** Count of distinct start/end windows among slots. */
export function groupParentUniqueDateCount(
  children: Pick<OpportunityGroupChildSnapshot, "startDate" | "endDate">[],
): number {
  return new Set(children.map(groupChildDateKey)).size;
}

/** Count of distinct locations among slots (incl. remote vs physical). */
export function groupParentUniqueLocationCount(
  children: Pick<OpportunityGroupChildSnapshot, "location" | "isRemote">[],
): number {
  return new Set(children.map(groupChildLocationKey)).size;
}

/** True when volunteer-visible slots span more than one start/end window. */
export function groupParentHasMultipleDates(
  children: Pick<OpportunityGroupChildSnapshot, "startDate" | "endDate">[],
): boolean {
  return groupParentUniqueDateCount(children) > 1;
}

/** Child slot visible to volunteers (aligned with core feed eligibility). */
export function isVolunteerVisibleGroupChild(
  child: {
    isDeleted?: boolean;
    isClosed?: boolean;
    isFull?: boolean;
    status?: string | null;
    lastCallToVolunteers: Date;
    startDate?: Date | null;
    endDate?: Date | null;
    participantsCount?: number;
    amount?: number;
  },
  now: Date = new Date(),
  minFreeSpaces: number = FEED_MIN_FREE_SPACES,
): boolean {
  if (child.isDeleted || child.isClosed) return false;
  if (child.status != null && child.status !== "APPROVED") return false;
  if (child.lastCallToVolunteers <= now) return false;
  const startTs = toTimestamp(child.startDate);
  const endTs = toTimestamp(child.endDate);
  const startOk = startTs == null || startTs >= now.getTime();
  const endOk = endTs == null || endTs >= now.getTime();
  if (!startOk || !endOk) return false;
  if (child.isFull) return false;
  const amount = child.amount ?? 1;
  const count = child.participantsCount ?? 0;
  if (count > amount - minFreeSpaces) return false;
  return true;
}

function childSnapshotHasVisibilityFields(
  child: OpportunityGroupChildSnapshot,
): boolean {
  return (
    child.isClosed !== undefined ||
    child.isDeleted !== undefined ||
    child.isFull !== undefined ||
    child.status !== undefined
  );
}

type UntreatedOverdueRow = {
  lastCallToVolunteers: Date;
  startDate: Date | null;
  endDate: Date | null;
};

/** Matches admin Opportunities table "לא טופלו" overdue signals for one row. */
export function opportunityRowMatchesUntreatedOverdue(
  row: UntreatedOverdueRow,
  now: Date = new Date(),
): boolean {
  if (row.lastCallToVolunteers <= now) return true;
  if (row.startDate != null && row.startDate <= now) return true;
  if (row.endDate != null && row.endDate <= now) return true;
  return false;
}

/** Child instance not yet passed — aligned with admin table open-tab date operators ($gte). */
export function opportunityRowStillOpenForAdminList(
  row: UntreatedOverdueRow,
  now: Date = new Date(),
): boolean {
  if (row.lastCallToVolunteers <= now) return false;
  if (row.startDate != null && row.startDate < now) return false;
  if (row.endDate != null && row.endDate < now) return false;
  return true;
}

/** Group parent appears in "לא טופלו" only when every active child instance has passed. */
export function groupParentQualifiesForUntreatedTab(
  children: Array<
    UntreatedOverdueRow & {
      isDeleted?: boolean;
    }
  >,
  now: Date = new Date(),
): boolean {
  const active = children.filter((c) => !c.isDeleted);
  if (active.length === 0) return false;
  return active.every((c) => opportunityRowMatchesUntreatedOverdue(c, now));
}

/** Group parent appears in "פתוחות" while any active child instance has not passed yet. */
export function groupParentQualifiesForFutureOpenTab(
  children: Array<
    UntreatedOverdueRow & {
      isDeleted?: boolean;
    }
  >,
  now: Date = new Date(),
): boolean {
  const active = children.filter((c) => !c.isDeleted);
  if (active.length === 0) return false;
  return active.some((c) => opportunityRowStillOpenForAdminList(c, now));
}

const activeGroupChildHyperFilter = { isDeleted: false };

/** Open-tab field signals for a single opportunity row (standalone or child instance). */
function opportunityRowFutureOpenFields(now: Date) {
  return {
    isDeleted: false,
    isClosed: false,
    isFull: false,
    lastCallToVolunteers: { $gt: now },
    $and: [
      { $or: [{ startDate: null }, { startDate: { $gte: now } }] },
      { $or: [{ endDate: null }, { endDate: { $gte: now } }] },
    ],
  };
}

/** "לא טופלו" field signals for a single opportunity row (standalone or child instance). */
function opportunityRowUntreatedOverdueFields(now: Date) {
  return {
    isDeleted: false,
    isClosed: false,
    isFull: false,
    $or: [
      { lastCallToVolunteers: { $lte: now } },
      { startDate: { $lte: now } },
      { endDate: { $lte: now } },
    ],
  };
}

/** Nested filter: group child instance still open (used on parent.childOpportunities). */
function groupChildStillOpenHyperFilter(now: Date) {
  return {
    isDeleted: false,
    isClosed: false,
    isFull: false,
    lastCallToVolunteers: { $gt: now },
    $and: [
      { $or: [{ startDate: null }, { startDate: { $gte: now } }] },
      { $or: [{ endDate: null }, { endDate: { $gte: now } }] },
    ],
  };
}

/** HyperFilter fragment: open signals for standalone rows or any open group child. */
export function futureOpenListHyperFilter(now: Date = new Date()) {
  return {
    $or: [
      {
        childOpportunities: {
          $some: groupChildStillOpenHyperFilter(now),
        },
      },
      {
        childOpportunities: { $none: activeGroupChildHyperFilter },
        ...opportunityRowFutureOpenFields(now),
      },
    ],
  };
}

/** HyperFilter fragment: overdue signals for standalone rows or all group children overdue. */
export function untreatedListOverdueHyperFilter(now: Date = new Date()) {
  return {
    $or: [
      {
        $and: [
          { childOpportunities: { $some: activeGroupChildHyperFilter } },
          {
            childOpportunities: {
              $none: groupChildStillOpenHyperFilter(now),
            },
          },
        ],
      },
      {
        childOpportunities: { $none: activeGroupChildHyperFilter },
        ...opportunityRowUntreatedOverdueFields(now),
      },
    ],
  };
}

/** Open-tab filter for a group child row in the opportunities table. */
export function groupChildRowFutureOpenHyperFilter(now: Date = new Date()) {
  return {
    parentOpportunityId: { $ne: null },
    ...opportunityRowFutureOpenFields(now),
  };
}

/** "לא טופלו" filter for a group child row in the opportunities table. */
export function groupChildRowUntreatedOverdueHyperFilter(now: Date = new Date()) {
  return {
    parentOpportunityId: { $ne: null },
    ...opportunityRowUntreatedOverdueFields(now),
  };
}

function parentOrStandaloneTabFields(now: Date, tab: "futureOpen" | "unHandled") {
  return {
    parentOpportunityId: null,
    isDeleted: false,
    isClosed: false,
    ...(tab === "futureOpen"
      ? futureOpenListHyperFilter(now)
      : untreatedListOverdueHyperFilter(now)),
  };
}

export type OpportunitiesTableTab =
  | "futureOpen"
  | "unHandled"
  | "history"
  | "full"
  | "archive";

/** Admin opportunities table: roots = parent/standalone rows only; all = include child instances. */
export type OpportunitiesTableRowScope = "roots" | "all";

export function opportunitiesTableRootRowsFilter(): {
  parentOpportunityId: null;
} {
  return { parentOpportunityId: null };
}

export type OpportunitiesTableTabHyperFilterOptions = {
  rowScope?: OpportunitiesTableRowScope;
};

/**
 * Tab filter for the admin opportunities table.
 * `roots` (default): group parents and standalone missions only.
 * `all`: also surfaces relevant child instance rows (legacy table behavior).
 */
export function opportunitiesTableTabHyperFilter(
  tab: OpportunitiesTableTab,
  now: Date = new Date(),
  options?: OpportunitiesTableTabHyperFilterOptions,
) {
  const rowScope = options?.rowScope ?? "roots";

  switch (tab) {
    case "futureOpen":
      return {
        $or: [
          parentOrStandaloneTabFields(now, "futureOpen"),
          ...(rowScope === "all"
            ? [groupChildRowFutureOpenHyperFilter(now)]
            : []),
        ],
      };
    case "unHandled":
      return {
        $or: [
          parentOrStandaloneTabFields(now, "unHandled"),
          ...(rowScope === "all"
            ? [groupChildRowUntreatedOverdueHyperFilter(now)]
            : []),
        ],
      };
    case "history":
      return { isDeleted: false, isClosed: true };
    case "full":
      return { isDeleted: false, isClosed: false };
    case "archive":
      return { isDeleted: true };
  }
}

export type GroupInstanceAdminStatus =
  | "closed"
  | "untreated"
  | "full"
  | "open";

export type GroupInstanceAdminWarning = "noCoordinates" | "lastCallSoon";

const GROUP_INSTANCE_LAST_CALL_WINDOW_MS = 24 * 60 * 60 * 1000;

/** Matches admin OpportunityTitle last-call indicator (within 24h or passed). */
export function isGroupInstanceLastCallSoonOrPassed(
  lastCallToVolunteers: Date | null | undefined,
  now: Date = new Date(),
): boolean {
  if (lastCallToVolunteers == null) return false;
  const lastTs = new Date(lastCallToVolunteers).getTime();
  if (Number.isNaN(lastTs)) return false;
  return lastTs <= now.getTime() + GROUP_INSTANCE_LAST_CALL_WINDOW_MS;
}

export type GroupInstanceAdminStatusInput = Pick<
  OpportunityGroupChildSnapshot,
  | "isClosed"
  | "isFull"
  | "participantsCount"
  | "amount"
  | "lastCallToVolunteers"
  | "startDate"
  | "endDate"
  | "coordinates"
>;

/** Primary status + secondary warnings for one group child in admin UI. */
export function computeGroupInstanceAdminStatus(
  child: GroupInstanceAdminStatusInput,
  now: Date = new Date(),
): {
  primary: GroupInstanceAdminStatus;
  warnings: GroupInstanceAdminWarning[];
} {
  let primary: GroupInstanceAdminStatus;
  if (child.isClosed) {
    primary = "closed";
  } else if (
    opportunityRowMatchesUntreatedOverdue(
      {
        lastCallToVolunteers: child.lastCallToVolunteers,
        startDate: child.startDate,
        endDate: child.endDate,
      },
      now,
    )
  ) {
    primary = "untreated";
  } else if (opportunitySeatsAreFull(child)) {
    primary = "full";
  } else {
    primary = "open";
  }

  const warnings: GroupInstanceAdminWarning[] = [];
  if (hasNoCoordinates(child.coordinates)) {
    warnings.push("noCoordinates");
  }
  if (isGroupInstanceLastCallSoonOrPassed(child.lastCallToVolunteers, now)) {
    warnings.push("lastCallSoon");
  }

  return { primary, warnings };
}

export function computeParentSyncFromChildren(
  children: OpportunityGroupChildSnapshot[],
  now: Date = new Date(),
): ParentSyncFromChildrenPayload | null {
  if (children.length === 0) return null;

  const notDeleted = children.filter((c) => !c.isDeleted);
  if (notDeleted.length === 0) return null;

  const usesFeedVisibility = notDeleted.some(childSnapshotHasVisibilityFields);

  const bookable = usesFeedVisibility
    ? notDeleted.filter((c) =>
        isVolunteerVisibleGroupChild(
          {
            isDeleted: c.isDeleted,
            isClosed: c.isClosed,
            isFull: c.isFull,
            status: c.status,
            lastCallToVolunteers: c.lastCallToVolunteers,
            startDate: c.startDate,
            endDate: c.endDate,
            participantsCount: c.participantsCount,
            amount: c.amount,
          },
          now,
        ),
      )
    : notDeleted.filter((c) => {
        if (
          c.startDate != null &&
          c.startDate < now &&
          (c.endDate == null || c.endDate < now)
        ) {
          return false;
        }
        if (c.endDate != null && c.endDate < now) return false;
        return true;
      });

  const openNotClosed = notDeleted.filter((c) => !c.isClosed);
  const pool =
    bookable.length > 0
      ? bookable
      : openNotClosed.length > 0
        ? openNotClosed
        : notDeleted;

  const first = [...pool].sort(
    (a, b) =>
      (toTimestamp(a.startDate) ?? Number.MAX_SAFE_INTEGER) -
      (toTimestamp(b.startDate) ?? Number.MAX_SAFE_INTEGER),
  )[0]!;

  const lastCall = notDeleted.reduce(
    (max, c) => (c.lastCallToVolunteers > max ? c.lastCallToVolunteers : max),
    notDeleted[0]!.lastCallToVolunteers,
  );

  const coordsChild =
    pool.find((c) => {
      if (c.isRemote) return false;
      const raw = c.coordinates as { lat?: number; lng?: number };
      const lat = Number(raw?.lat);
      const lng = Number(raw?.lng);
      return Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0);
    }) ?? first;

  return {
    startDate: first.startDate,
    endDate: first.endDate,
    lastCallToVolunteers: lastCall,
    coordinates: coordsChild.coordinates,
    isRemote: pool.every((c) => c.isRemote),
    location: first.isRemote ? "מרחוק" : first.location.trim(),
  };
}
